import { TranslationService } from './../../services/translation.service';
import {
  ChangeDetectorRef,
  Component,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ColumnMode, NgxDatatableModule } from '@swimlane/ngx-datatable';
import {
  trigger,
  state,
  style,
  animate,
  transition,
} from '@angular/animations';
import { Subscription } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { PanelModule } from 'primeng/panel';
import { InputTextModule } from 'primeng/inputtext';
import { User } from '../../interfaces';
import { UserHttpService } from '../../services';
import { TranslatePipe } from '@ngx-translate/core';
import { API_URL } from '../../utils';

@Component({
  selector: 'app-manage-users',
  templateUrl: './manage-users.component.html',
  styleUrls: ['./manage-users.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('datatableAnimation', [
      transition(':enter', [
        // :enter is alias for void => *
        style({ height: '0px', visibility: 'hidden', opacity: 0 }),
        animate(
          '225ms ease-in',
          style({ height: '*', visibility: 'visible', opacity: 1 })
        ),
      ]),
      transition(':leave', [
        // :leave is alias for * => void
        style({ height: '*', visibility: 'visible', opacity: 1 }),
        animate(
          '225ms ease-out',
          style({ height: '0px', visibility: 'hidden', opacity: 0 })
        ),
      ]),
    ]),
    trigger('slideUpDown', [
      state(
        'expanded',
        style({
          height: '*', // Use the actual height of the element
          opacity: 1,
          overflow: 'hidden',
        })
      ),
      state(
        'collapsed',
        style({
          height: '0', // Collapse the element to zero height
          opacity: 0,
          overflow: 'hidden',
        })
      ),
      transition('expanded <=> collapsed', [
        animate('300ms ease-in-out'), // Duration and easing of the animation
      ]),
    ]),
  ],
  standalone: true,
  imports: [
    NgxDatatableModule,
    RouterLink,
    FormsModule,
    ButtonModule,
    PanelModule,
    InputTextModule,
    TranslatePipe
  ],
})
export class ManageUsersComponent implements OnInit, OnDestroy {
  translateService = inject(TranslationService);
  loadingData: boolean = true;
  userStatus: number = 0;
  rows: User[] = [];
  filteredRows: User[] = [];
  expandedRows: Set<any> = new Set();
  users: User[] = [];
  @ViewChild('myTable') table: any;
  expanded: any = {};
  timeout: any;
  ColumnMode = ColumnMode;
  temp: User[] = [];
  showFilters: boolean = true;
  isSmallScreen: boolean = window.innerWidth < 991;
  suspiciousUsersCount: number = 0;
  private subscriptions: Subscription[] = [];
  api = inject(API_URL)
  suspiciousChecked: boolean = false;
  filterValue: string = '';

  columns = [
    { prop: 'userId', name: 'MANAGE_USERS.USER_ID' },
    { prop: 'username', name: 'MANAGE_USERS.USERNAME' },
    { prop: 'email', name: 'MANAGE_USERS.SEARCH_BY_EMAIL' },
    { prop: 'firstName', name: 'MANAGE_USERS.FIRST_NAME' },
    { prop: 'lastName', name: 'MANAGE_USERS.LAST_NAME' },
    { prop: 'role', name: 'MANAGE_USERS.ROLE' },
    { prop: 'createdDate', name: 'MANAGE_USERS.CREATED_DATE' },
    { prop: 'updatedDate', name: 'MANAGE_USERS.UPDATED_DATE' },
  ];

  constructor(
    private userService: UserHttpService,
    private cd: ChangeDetectorRef,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe((p) => {
      this.userStatus = +p['id'];
    });
    const userSubscription$ = this.userService
      .getUsersV1()
      .subscribe((result) => {
        if (result) {
          //
          this.loadingData = false;
          this.users = this.processUsers(result);
          this.rows = this.users;
          this.temp = [...this.rows];
          this.filteredRows = [...this.rows];
        }
      });
    this.subscriptions.push(userSubscription$);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
  onPage(event: any) {
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
    }, 100);
  }

  toggleExpandRow(row: any) {
    this.table.rowDetail.toggleExpandRow(row);
    row.expanded = !row.expanded;
    this.rows = [...this.rows];
  }
  onDetailToggle(event: { value: any }) {
    const row = event.value;
    if (this.expandedRows.has(row)) {
      this.expandedRows.delete(row);
      row.expanded = false;
    } else {
      this.expandedRows.add(row);
      row.expanded = true;
    }

    this.rows = [...this.rows];
  }

  processUsers(users: any[]): any[] {
    return users.map((userItem) => {
      return {
        userId: userItem.userId,
        username: userItem.username,
        passwordHash: userItem.passwordHash,
        email: userItem.email,
        firstName: userItem.firstName,
        lastName: userItem.lastName,
        role: userItem.role,
        isActive: userItem.isActive == true ? 'True' : 'False',
        createdDate: new Date(userItem.createdDate).toLocaleDateString('en-GB'),
        updatedDate: new Date(userItem.updatedDate).toLocaleDateString('en-GB'),
      };
    });
  }

  syncInvestigators(): void {

  this.userService.syncInvestigators().subscribe({
    error: (err) => {
      console.error('Failed to sync investigators', err);
    }
  });
}

  filterByEmail() {
    const email = this.filterValue.toLowerCase().trim(); // Get the filter value
    if (email === '') {
      this.filteredRows = [...this.rows]; // Show all data when filter is empty
    } else {
      this.filteredRows = this.rows.filter(
        (row) => row.email && row.email.toLowerCase().includes(email)
      );
    }
  }

  toggleFilters() {
    this.showFilters = !this.showFilters;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.isSmallScreen = window.innerWidth < 991;
  }

  shouldEnableHorizontalScroll(): boolean {
    return this.isSmallScreen;
  }

  getColumnWidth(columnName: string): number {
    if (this.isSmallScreen) {
      switch (columnName) {
        case 'userId':
          return 80;
        case 'username':
          return 200;
        case 'email':
          return 300;
        case 'firstName':
          return 200;
        case 'lastName':
          return 200;
        default:
          return 200;
      }
    } else {
      switch (columnName) {
        case 'userId':
          return 120;
        case 'username':
          return 200;
        case 'email':
          return 250;
        case 'firstName':
          return 200;
        case 'lastName':
          return 200;
        default:
          return 150;
      }
    }
  }

}
